import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { defer, Observable, switchMap } from 'rxjs';
import { loadAndAssert } from '../authz/access.js';
import { Exam } from '../exam.js';
import { Submission } from '../submission.js';

function normalizeSubmissionsData(submissions: any[]): Submission[] {
  const normalizeBoundingBoxes = (boxes: any[] | undefined | null) => (boxes || []).filter(Boolean).map((box: any) => ({
      ...box,
      _class: box?._class || 'BoundingBox',
      topLeft: {
        ...(box?.topLeft || {}),
        _class: box?.topLeft?._class || 'Point',
      },
      bottomRight: {
        ...(box?.bottomRight || {}),
        _class: box?.bottomRight?._class || 'Point',
      },
    }));

  const normalizeTask = (task: any) => ({
    ...task,
    _class: task?._class || 'SubmissionTask',
    gradingResult: task?.gradingResult
      ? { ...task.gradingResult, _class: task.gradingResult?._class || 'GradingResult' }
      : task?.gradingResult,
    overallGradingResult: task?.overallGradingResult
      ? { ...task.overallGradingResult, _class: task.overallGradingResult?._class || 'GradingResult' }
      : task?.overallGradingResult,
    boundingBoxes: normalizeBoundingBoxes(task?.boundingBoxes),
    subTasksSubmission: (task?.subTasksSubmission || []).filter(Boolean).map((child: any) => normalizeTask(child)),
  });

  return submissions.map((submission: any) => ({
    ...submission,
    taskSubmissions: (submission?.taskSubmissions || []).filter(Boolean).map((task: any) => normalizeTask(task)),
  })) as Submission[];
}

export const getSubmissionsByExamIdQuery = registerQuery({
  operationName: 'getSubmissionsByExamIdQuery',
  type: () => [Submission],
  params: [{ name: 'examId', type: () => String }],
  resolve: (examId: string) => async (selection: BaseQLSelectionSet<Submission>) => {
    // Listing by examId is only as private as the exam itself (also called from structure-step in server context).
    await loadAndAssert(Exam, examId, 'read', { _id: 1 });
    const submissionCollection = await getCollection(Submission);
    const submissions = await submissionCollection.find({ examId }).toArray();

    return normalizeSubmissionsData(submissions);
  },
});

export const streamSubmissionsByExamIdSubscription = registerSubscription({
  operationName: 'streamSubmissionsByExamIdSubscription',
  type: () => [Submission],
  params: [{ name: 'examId', type: () => String }],

  resolve: (examId: string) => (_selection: BaseQLSelectionSet<Submission>) =>
    new Observable<Submission[]>(subscriber => {
      const sub = watchQuery(Submission._collectionName, { examId }).subscribe({
        next: rows => {
          const rawSubmissions = Array.isArray(rows) ? rows : [];

          const normalizedSubmissions = normalizeSubmissionsData(rawSubmissions);

          subscriber.next(normalizedSubmissions);
        },
        error: err => subscriber.error(err),
        complete: () => subscriber.complete(),
      });

      return () => {
        sub.unsubscribe();
      };
      // watchQuery is unfiltered: assert read on the exam once, on subscribe, before watching its submissions.
    }).pipe(source => defer(() => loadAndAssert(Exam, examId, 'read', { _id: 1 })).pipe(switchMap(() => source))),
});
